import React from 'react';
import { heldNow } from '../../lib/retainage';

/* ──────────────────────────────────────────────────────────────────
 * BillingG702 — Region 1 header card
 *
 * AIA G702 "Application for Payment" header. Two-column layout:
 * left column = AFP # title, status pill, CE-sync chip, period range
 * line. Right column = 2×3 grid of compact KPI tiles. Each KPI tile
 * follows the empty-state muting rule (value drops to --text-4 at $0,
 * label stays at --text-3).
 * ────────────────────────────────────────────────────────────────── */

const $f = (v) => {
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return '$0.00';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDateMD = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const CheckIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const AlertTriIcon = ({ size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

const StatusPill = ({ status }) => {
  const isDraft = status === 'Draft';
  const fg = isDraft ? 'var(--text-3)' : 'var(--success)';
  const bg = isDraft ? 'var(--bg-surface-2)' : 'var(--success-soft)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 9px 2px 8px', borderRadius: 999,
      fontSize: 12.5, fontWeight: 500, color: fg, background: bg,
      border: `1px solid color-mix(in oklch, ${fg} 22%, transparent)`,
      letterSpacing: '-0.005em', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 99, background: fg }} />
      {status}
    </span>
  );
};

const InlineCEChip = ({ period }) => {
  if (period.status !== 'Finalized') return null;
  const synced = period.ce_sync_status === 'success';
  const fg = synced ? 'var(--success)' : 'var(--warning)';
  const bg = synced ? 'var(--success-soft)' : 'var(--warning-soft)';
  return (
    <span title={synced ? 'Synced to ComputerEase' : (period.ce_sync_error || 'CE sync pending')} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 7px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 500, color: fg, background: bg,
      border: `1px solid color-mix(in oklch, ${fg} 22%, transparent)`,
      letterSpacing: '-0.005em',
    }}>
      {synced ? <CheckIcon /> : <AlertTriIcon />}
      {synced ? 'CE Synced' : 'Needs CE Sync'}
    </span>
  );
};

const Kpi = ({ label, value, isDollar = true, accent, sub }) => {
  const n = isDollar ? (parseFloat(value) || 0) : value;
  const muted = isDollar ? (!n || n === 0) : (!value);
  return (
    <div>
      <div style={{
        fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
        marginBottom: 4, whiteSpace: 'nowrap',
      }}>{label}</div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontFamily: 'var(--font-mono)', fontSize: 16.5, fontWeight: 500,
        color: muted ? 'var(--text-4)' : 'var(--text-1)',
        letterSpacing: '-0.02em',
      }}>
        {isDollar ? $f(value) : (value || '–')}
        {accent && !muted && (
          <span style={{ width: 5, height: 5, borderRadius: 99, background: accent, flexShrink: 0 }} />
        )}
      </div>
      {sub && (
        <div style={{
          fontSize: 12, color: 'var(--text-3)', marginTop: 2,
        }}>{sub}</div>
      )}
    </div>
  );
};

const BillingG702 = ({ period, details, onInvoiceDateChange, savingInvoiceDate, ceState = null }) => {
  if (!period) return null;

  const isDraft = period.status === 'Draft';

  // KPI source values are computed from the per-line billing_period_line
  // rows that arrive in `details.lines` (each row has a
  // billing_period_id + sov_line_id pair). Sums here, not on the server,
  // so the tiles update live as the PM inline-edits %/stored materials
  // in the G703 below without needing a round-trip per keystroke.
  const lines = (details && details.lines) || [];
  const num = (v) => parseFloat(v) || 0;

  // 1. Scheduled Value -- total contract value for this SOV. Per AIA G703
  //    this is Column C (per-line) -> the line total at the top of the
  //    invoice. Same on every period of the project (until COs change).
  const scheduledValue = lines.reduce((s, l) => s + num(l.contract_amount), 0);

  // 2. Billed To Date (NET cumulative) -- everything ever invoiced on
  //    this contract, after retainage. Sum of: cumulative gross earned
  //    & stored across all effective periods, minus the cumulative
  //    retainage held. billed_to_date on each billing_period_line is
  //    the PRIOR cumulative gross (from generateLinesForPeriod's
  //    previousValues.billed_to_date) -- adding this period's
  //    total_completed_and_stored gives the gross-through-now; subtract
  //    retainage_amount (which is itself cumulative) to get net.
  const cumGrossEarned = lines.reduce(
    (s, l) => s + num(l.billed_to_date) + num(l.total_completed_and_stored),
    0);
  // Held NOW: the cumulative retainage less every release to date (lib/retainage.js).
  const cumRetainage = lines.reduce((s, l) => s + heldNow(l), 0);
  const billedToDate = cumGrossEarned - cumRetainage;

  // 3. Retainage Held -- retainage withheld to date, less what's been released. Already summed above as cumRetainage.
  const retainageHeld = cumRetainage;

  // 4. Billed This Period -- net amount payable for THIS AFP only
  //    (current period's gross work, minus the incremental retainage
  //    increase). total_billable_this_period is exactly that figure
  //    per line, computed by generateLinesForPeriod.
  const billedThisPeriod = lines.reduce(
    (s, l) => s + num(l.total_billable_this_period), 0);

  // 5. % Billed of Scheduled Value -- progress indicator. Net-billed
  //    matches the Billed To Date concept above, so the percentage
  //    answers "how much of the contract have we invoiced". Capped at
  //    100% for display (over-billing edge cases shouldn't read as
  //    e.g. 102%).
  const pctBilled = scheduledValue > 0
    ? Math.min(100, (billedToDate / scheduledValue) * 100)
    : 0;

  // 6. Billing Cycle # -- chronological position of this period among
  //    the project's periods. Computed server-side in GET /:id so it
  //    auto-renumbers when periods are inserted / deleted / reordered.
  const cycleNumber = period.cycle_number || null;

  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
      padding: '18px 22px',
      display: 'grid',
      gridTemplateColumns: '60% 1px 40%',
      gap: 0,
    }}>
      {/* Left column */}
      <div style={{ paddingRight: 24 }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: 'var(--text-3)',
          textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
        }}>Application for Payment</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <h2 style={{
            margin: 0, fontSize: 23.5, fontWeight: 600, color: 'var(--text-1)',
            letterSpacing: '-0.02em', lineHeight: 1.15,
          }}>{period.period_name || `AFP #${period.id}`}</h2>
          <StatusPill status={period.status} />
          {/* Only what the agent reported (BillingCEBanner's ceStateOf): the old "Needs CE Sync" chip guessed from
              ce_synced, which an Azure idle-timeout left at 0 on pay apps that had pushed fine. */}
          {ceState && (
            <span style={{
              fontSize: 12.5, fontWeight: 500, padding: '3px 9px', borderRadius: 99,
              color: 'var(--warning)', background: 'var(--warning-soft)',
              border: '1px solid color-mix(in oklch, var(--warning) 30%, transparent)',
            }}>Not in ComputerEase yet</span>
          )}
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--text-3)' }}>
          Period{' '}
          <span className="mono" style={{ color: 'var(--text-2)' }}>
            {fmtDateMD(period.period_start_date)} – {fmtDateMD(period.period_end_date)}
          </span>
          {/* Editable while Draft. The invoice date prints on the AFP in CE and
              starts the 30-day clock that drives AR aging, so it has to be
              correctable before the application is issued -- and locked after.
              The server enforces that too: it refuses the change on a
              Finalized period and tells you to revise instead. */}
          {isDraft && onInvoiceDateChange ? (
            <>
              {' · Invoice date '}
              <input
                type="date"
                value={period.invoice_date || ''}
                disabled={savingInvoiceDate}
                onChange={(e) => { if (e.target.value) onInvoiceDateChange(e.target.value); }}
                title="Date this application is issued. Payment terms run 30 days from here."
                className="mono"
                style={{
                  fontSize: 13, padding: '1px 6px', borderRadius: 6,
                  color: 'var(--text-2)', background: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)', fontFamily: 'inherit',
                  cursor: savingInvoiceDate ? 'wait' : 'pointer',
                  opacity: savingInvoiceDate ? 0.6 : 1,
                }}
              />
              {period.due_date ? (
                <span style={{ color: 'var(--text-4)' }}>
                  {' · due '}<span className="mono">{fmtDateMD(period.due_date)}</span>
                </span>
              ) : null}
            </>
          ) : period.invoice_date ? (
            <>
              {' · Invoice date '}
              <span className="mono" style={{ color: 'var(--text-2)' }}>{fmtDateMD(period.invoice_date)}</span>
              {period.due_date ? (
                <span style={{ color: 'var(--text-4)' }}>
                  {' · due '}<span className="mono">{fmtDateMD(period.due_date)}</span>
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {/* Vertical divider */}
      <div style={{ background: 'var(--border-subtle)', width: 1, height: '100%' }} />

      {/* Right column — 2×3 KPI grid */}
      <div style={{
        paddingLeft: 24,
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
        rowGap: 16, columnGap: 20,
      }}>
        <Kpi label="Scheduled Value" value={scheduledValue} />
        <Kpi label="Billed To Date" value={billedToDate} />
        <Kpi
          label="Retainage Held"
          value={retainageHeld}
          accent={retainageHeld > 0 ? 'var(--warning)' : null}
        />
        <Kpi
          label="Billed This Period"
          value={billedThisPeriod}
          accent={isDraft && billedThisPeriod > 0 ? 'var(--accent)' : null}
          sub={isDraft && billedThisPeriod > 0 ? 'Pending finalize' : null}
        />
        <Kpi
          label="% Billed"
          value={pctBilled.toFixed(1) + '%'}
          isDollar={false}
        />
        <Kpi
          label="Billing Cycle"
          value={cycleNumber ? `#${cycleNumber}` : null}
          isDollar={false}
        />
      </div>
    </div>
  );
};

export default BillingG702;
